import {
  VStack,
  Box,
  Image,
  Text,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import CampaignBadge from "../Components/Badge";
import { KYDInternalCampaign } from "@common/interfaces/KYDCampaign";
import actions from "src/actions";
import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { findImageForCampaign } from "../utils/findImageForCampaign";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";
import { KYDScope } from "@common/interfaces/ICognito";

const DraftCampaigns = ({
  campaigns,
  permissions,
  invalidateCampaigns,
}: {
  campaigns: KYDInternalCampaign[];
  permissions?: KYDScope[];
  invalidateCampaigns: () => Promise<void>;
}) => {
  const [updatingCampaignId, setUpdatingCampaignId] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const toast = useToast();
  const sortedCampaigns = useMemo(
    () =>
      [...campaigns].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ),
    [campaigns]
  );
  const handleDuplicate = async (id: string) => {
    try {
      setUpdatingCampaignId(id);
      const { id: newCampaignId } = await actions.ADMIN.duplicateCampaign(id);
      toast({
        title: "Campaign duplicated",
        description: "Campaign duplicated successfully",
        status: "success",
      });
      navigate(`/campaigns/create?id=${newCampaignId}`);
      await invalidateCampaigns();
    } catch (error) {
      console.error("Error duplicating campaign:", error);
      toast({
        title: "Error duplicating campaign",
        description: error.message,
        status: "error",
      });
    }
    setUpdatingCampaignId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      setUpdatingCampaignId(id);
      await actions.ADMIN.deleteCampaign(id);
      toast({
        title: "Campaign deleted",
        description: "Campaign deleted successfully",
        status: "success",
      });
      invalidateCampaigns();
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast({
        title: "Error deleting campaign",
        description: error.message,
        status: "error",
      });
    }
    setUpdatingCampaignId(null);
  };

  return (
    <VStack alignItems="flex-start" py={4} w="100%">
      <Text fontSize="x-large" fontWeight="600" mb="2">
        Draft Campaigns
      </Text>

      <Box position="relative" width="100%">
        <HStack width="100%" overflowX="auto" gap="4">
          {sortedCampaigns.map((c) => {
            const image = findImageForCampaign(c);

            return (
              <HStack
                key={c.id}
                flexShrink={0}
                background="linear-gradient(0deg, #F7F7F7, #F7F7F7),
                linear-gradient(180deg, rgba(231, 233, 239, 0.0075) 0%, rgba(255, 255, 255, 0.75) 100%)"
                position="relative"
                border="1px solid #DDDDDD"
                borderRadius="10px"
                padding="12px"
                gap="12px"
                width="320px"
                boxShadow="0px 4px 12px -5px #00000009, 0px 1px 2px 0px #FFFFFFBF inset"
                cursor="pointer"
                onClick={() => navigate(`/campaigns/create?id=${c.id}`)}
              >
                <Image
                  src={image || ""}
                  alt={c.name}
                  w="150px"
                  h="150px"
                  aspectRatio="1"
                  borderRadius="2px"
                  objectFit="cover"
                />

                <VStack align="flex-start" spacing="8px">
                  <CampaignBadge type={c.type} variant="outline" />
                  <Text
                    noOfLines={2}
                    fontSize="16px"
                    fontWeight="600"
                    lineHeight="20px"
                  >
                    {c.name}
                  </Text>

                  <Box>
                    <Text fontSize="12px" color="#00000080">
                      Edited on
                    </Text>
                    <Text fontSize="12px" color="#00000080">
                      {new Date(c.updated_at).toLocaleDateString()}
                    </Text>
                  </Box>
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      size="sm"
                      rightIcon={<ChevronDownIcon />}
                      fontWeight="500"
                      color="#1D74FF"
                      px={0}
                      isLoading={updatingCampaignId === c.id}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Resume editing
                    </MenuButton>
                    <Portal>
                      <MenuList fontSize={"13px"}>
                        <MenuItem
                          as={RouterLink}
                          to={`/campaigns/create?id=${c.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Resume Editing
                        </MenuItem>
                        <ShowIfHasScope
                          permissions={permissions}
                          checkFor={["crm/campaigns:write", "crm/write"]}
                        >
                          <>
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicate(c.id);
                              }}
                            >
                              Duplicate
                            </MenuItem>
                            <Divider borderColor={"#00000033"} />
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(c.id);
                              }}
                            >
                              Delete Draft
                            </MenuItem>
                          </>
                        </ShowIfHasScope>
                      </MenuList>
                    </Portal>
                  </Menu>
                </VStack>
              </HStack>
            );
          })}
        </HStack>
      </Box>
    </VStack>
  );
};

export default DraftCampaigns;
